import { useCallback, useEffect, useMemo, useState, Fragment, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiUrl } from "../lib/api";
import { downloadDocument } from "../lib/fetchDocument";
import { recipientStatusLabel, recipientStatusTone, relativeTime } from "../lib/format";
import { FileBrowser, type Doc, type Folder } from "../components/FileBrowser";
import { EmptyState } from "../components/EmptyState";
import { SkeletonPage } from "../components/Skeleton";
import { IconLink, IconPerson, IconSearch } from "../components/Icons";
import { Modal } from "../components/Modal";
import { Toast } from "../components/Toast";
import { VisitorGatePreview } from "../components/VisitorGatePreview";
import { BrandLoader } from "../components/BrandLoader";
import { useLiveVisitFeed } from "../hooks/useLiveVisitFeed";

type Recipient = {
  id: string;
  email: string;
  name?: string;
  status: string;
  score?: number;
  label?: string;
  momentum_7d?: number;
  link_id?: string;
  last_active_at?: string | null;
  docs_touched?: number;
  total_dwell_ms?: number;
  link_expires_at?: string | null;
  require_nda?: number;
  allow_download?: number;
  watermark_enabled?: number;
  has_password?: number;
  revoked_at?: string | null;
  nda_signed_at?: string | null;
  nda_full_name?: string | null;
};
type NdaAcceptance = {
  id: string;
  full_name: string;
  version: number;
  accepted_at: string;
  recipient_email: string;
  recipient_name?: string | null;
  signature_data_url?: string | null;
  view_id?: string | null;
};
type Room = {
  id: string;
  name: string;
  room_type: string;
  nda_required: number;
  download_policy: string;
  welcome_message?: string;
  banner_url?: string;
  logo_url?: string;
  watermark_enabled?: number;
  description?: string;
};
type AnalyticsDoc = {
  id: string;
  filename: string;
  page_count?: number;
  total_dwell: number;
  unique_viewers: number;
};
type LinkHistoryItem = {
  id: string;
  created_at: string;
  revoked_at?: string | null;
  expires_at?: string | null;
  use_count: number;
  session_count: number;
  last_session_at?: string | null;
};

const TAB_LABELS: Record<"content" | "recipients" | "analytics" | "access" | "audit", string> = {
  content: "Files",
  recipients: "Share",
  analytics: "Analytics",
  access: "Access",
  audit: "Audit",
};

const ROOM_TYPE_LABELS: Record<string, string> = {
  ma: "M&A",
  ma_sellside: "M&A sell-side",
  li_holding: "LI holding",
  real_estate: "Real estate",
  investor_ir: "Investor relations",
  franchise: "Franchise",
  client_portal: "Client portal",
  closing_archive: "Closing archive",
};

function roomTypeLabel(roomType: string): string {
  if (ROOM_TYPE_LABELS[roomType]) return ROOM_TYPE_LABELS[roomType];
  const human = roomType.replace(/_/g, " ").trim();
  return human ? human.charAt(0).toUpperCase() + human.slice(1) : "Space";
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  "access.denied_email": "Access denied (email)",
  "document.view": "Viewed document",
  "document.download": "Downloaded document",
  "document.upload": "Uploaded document",
  "document.delete": "Deleted document",
  "folder.create": "Created folder",
  "folder.delete": "Deleted folder",
  "link.create": "Created link",
  "link.update": "Updated link settings",
  "nda.accept": "Signed NDA",
  "nda.signed": "Signed NDA",
  "recipient.invite": "Invited recipient",
  "recipient.revoke": "Revoked access",
  "room.create": "Created Space",
  "room.clone": "Cloned Space",
  "room.delete": "Deleted Space",
  "room.nda_updated": "Updated NDA",
  "session.start": "Opened Space",
};

function auditActionLabel(action: unknown): string {
  const a = String(action ?? "").trim();
  if (AUDIT_ACTION_LABELS[a]) return AUDIT_ACTION_LABELS[a];
  const human = a.replace(/[._]+/g, " ").trim();
  return human ? human.charAt(0).toUpperCase() + human.slice(1) : "Activity";
}

function auditActorLabel(event: Record<string, unknown>): string {
  const pick = (v: unknown) => {
    if (typeof v !== "string") return null;
    const s = v.trim();
    return s && s !== "undefined" && s !== "null" ? s : null;
  };
  const direct = pick(event.actor_email) ?? pick(event.actor_name) ?? pick(event.actor_id);
  if (direct) return direct;
  const type = pick(event.actor_type);
  if (type === "admin") return "Admin";
  if (type === "visitor") return "Visitor";
  return "System";
}

function linkExpiryBadge(expiresAt?: string | null) {
  if (!expiresAt) return null;
  const t = new Date(expiresAt).getTime();
  if (Number.isNaN(t)) return null;
  const msLeft = t - Date.now();
  if (msLeft <= 0) return <span className="badge warn">Expired</span>;
  const days = Math.floor(msLeft / 86_400_000);
  const label =
    days < 1
      ? "Expires today"
      : days === 1
        ? "Expires tomorrow"
        : days < 30
          ? `Expires in ${days}d`
          : `Expires ${new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  return <span className="badge">{label}</span>;
}

export function RoomDetailPage() {
  const { roomId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setup = searchParams.get("setup") === "1";
  const [tab, setTab] = useState<"content" | "recipients" | "analytics" | "access" | "audit">("content");
  const [room, setRoom] = useState<Room | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<Doc[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const inviteBusyRef = useRef(false);
  const [resendBusyId, setResendBusyId] = useState<string | null>(null);
  const resendBusyRef = useRef(false);
  const [shareMode, setShareMode] = useState<"email" | "link">("link");
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<Recipient[]>([]);
  const [analyticsDocs, setAnalyticsDocs] = useState<AnalyticsDoc[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [docDropoff, setDocDropoff] = useState<Array<{ page: number; pct: number }>>([]);
  const [expandedRecipient, setExpandedRecipient] = useState<string | null>(null);
  const [linkHistory, setLinkHistory] = useState<Record<string, LinkHistoryItem[]>>({});
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientSort, setRecipientSort] = useState<"last_active" | "score" | "email" | "status">("last_active");
  const [audit, setAudit] = useState<Array<Record<string, unknown>>>([]);
  const [ndaText, setNdaText] = useState("");
  const [ndaTitle, setNdaTitle] = useState("Non-Disclosure Agreement");
  const [ndaAcceptances, setNdaAcceptances] = useState<NdaAcceptance[]>([]);
  const [ndaLoaded, setNdaLoaded] = useState(false);
  const [ndaSaving, setNdaSaving] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [shareEmptyConfirm, setShareEmptyConfirm] = useState(false);
  const [cloneConfirm, setCloneConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [cloneBusy, setCloneBusy] = useState(false);
  const cloneBusyRef = useRef(false);
  const [importBusy, setImportBusy] = useState(false);
  const importBusyRef = useRef(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const revokeBusyRef = useRef(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [brandingDraft, setBrandingDraft] = useState({
    welcome_message: "",
    banner_url: "",
    logo_url: "",
    watermark_enabled: false,
  });
  const liveFeed = useLiveVisitFeed(
    roomId ? `/api/v1/analytics/rooms/${roomId}/live` : null,
    tab === "analytics",
  );
  const liveEventsLenRef = useRef(0);
  const roomLoadGenRef = useRef(0);
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;
  const docAnalyticsReqRef = useRef(0);

  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["content", "recipients", "analytics", "access", "audit"] as const;

  const setTabAndUrl = (t: (typeof validTabs)[number]) => {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tabFromUrl && (validTabs as readonly string[]).includes(tabFromUrl)) {
      setTab(tabFromUrl as (typeof validTabs)[number]);
    }
  }, [tabFromUrl]);

  const reload = useCallback(async () => {
    const forRoom = roomId;
    // Late mutation reloads closed over a prior Space must not steal the gen.
    if (roomIdRef.current !== forRoom) return;
    const gen = ++roomLoadGenRef.current;
    setLoadErr(null);
    try {
      const [r, files, rec] = await Promise.all([
        api<{ room: Room }>(`/api/v1/rooms/${forRoom}`),
        api<{ folders: Folder[]; documents: Doc[] }>(`/api/v1/rooms/${forRoom}/files`),
        api<{ recipients: Recipient[] }>(`/api/v1/rooms/${forRoom}/recipients`),
      ]);
      // Reject stale gens and late mutation reloads closed over a prior roomId.
      if (gen !== roomLoadGenRef.current || roomIdRef.current !== forRoom) return;
      setRoom(r.room);
      setBrandingDraft({
        welcome_message: r.room.welcome_message ?? "",
        banner_url: r.room.banner_url ?? "",
        logo_url: r.room.logo_url ?? "",
        watermark_enabled: Boolean(r.room.watermark_enabled),
      });
      setFolders(files.folders);
      setDocuments(files.documents);
      setRecipients(rec.recipients);
    } catch (e) {
      if (gen !== roomLoadGenRef.current || roomIdRef.current !== forRoom) return;
      setLoadErr(String((e as Error).message || e));
    } finally {
      if (gen === roomLoadGenRef.current && roomIdRef.current === forRoom) setLoading(false);
    }
  }, [roomId]);

  const loadNda = useCallback(async () => {
    const forRoom = roomId;
    const gen = roomLoadGenRef.current;
    const res = await api<{ nda: { body_html?: string; title?: string } | null; acceptances: NdaAcceptance[] }>(
      `/api/v1/rooms/${forRoom}/nda`,
    );
    if (gen !== roomLoadGenRef.current || roomIdRef.current !== forRoom) return;
    setNdaText(res.nda?.body_html ?? "");
    setNdaTitle(res.nda?.title ?? "Non-Disclosure Agreement");
    setNdaAcceptances(res.acceptances ?? []);
    setNdaLoaded(true);
  }, [roomId]);

  useEffect(() => {
    setLoading(true);
    setRoom(null);
    setFolders([]);
    setDocuments([]);
    setRecipients([]);
    setFolderId(null);
    setExpandedRecipient(null);
    setLinkHistory({});
    setNdaText("");
    setNdaTitle("Non-Disclosure Agreement");
    setNdaAcceptances([]);
    setNdaLoaded(false);
    setLeaderboard([]);
    setAnalyticsDocs([]);
    setSelectedDocId(null);
    setDocDropoff([]);
    setAudit([]);
    setLastLink(null);
    setEmail("");
    setInviteName("");
    setCsvText("");
    setShowCsv(false);
    setRevokeId(null);
    docAnalyticsReqRef.current += 1;
    reload().catch(console.error);
  }, [reload]);

  useEffect(() => {
    if (tab === "analytics") loadAnalytics().catch(console.error);
    if (tab === "audit") loadAudit().catch(console.error);
    if (tab === "access") loadNda().catch(console.error);
  }, [tab, roomId, loadNda]);

  useEffect(() => {
    if (!room?.nda_required) {
      setNdaAcceptances([]);
      setNdaLoaded(false);
      return;
    }
    loadNda().catch(console.error);
  }, [room?.nda_required, roomId, loadNda]);

  useEffect(() => {
    if (tab !== "analytics") return;
    if (liveFeed.events.length > liveEventsLenRef.current) {
      const newest = liveFeed.events[0];
      if (newest?.type === "session_start") loadAnalytics().catch(console.error);
    }
    liveEventsLenRef.current = liveFeed.events.length;
  }, [liveFeed.events.length, liveFeed.events[0]?.at, tab]);

  const flash = (msg: string) => setToast(msg);

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flash("Link copied");
    } catch {
      flash("Could not copy. Select and copy manually");
    }
  };

  const doInvite = async () => {
    const invited = email.trim();
    if (!invited || inviteBusyRef.current) return;
    inviteBusyRef.current = true;
    setInviteBusy(true);
    try {
      const res = await api<{ urlPath: string; token: string }>(`/api/v1/rooms/${roomId}/recipients`, {
        method: "POST",
        body: JSON.stringify({
          email: invited,
          name: inviteName || undefined,
          sendInvite: true,
        }),
      });
      const url = `${window.location.origin}/v/${res.token}`;
      setLastLink(url);
      setEmail("");
      setInviteName("");
      await reload();
      if (shareMode === "link") {
        try {
          await navigator.clipboard.writeText(url);
          flash("Invite sent and link copied");
        } catch {
          flash("Invite sent. Could not copy. Select and copy manually");
        }
      } else {
        flash(`Invite sent to ${invited}`);
      }
    } finally {
      inviteBusyRef.current = false;
      setInviteBusy(false);
    }
  };

  const invite = async () => {
    if (!email.trim() || inviteBusyRef.current) return;
    if (!documents.some((d) => d.status === "ready")) {
      setShareEmptyConfirm(true);
      return;
    }
    await doInvite();
  };

  const copyRecipientLink = async (recipientId: string) => {
    try {
      const res = await api<{ token: string }>(`/api/v1/rooms/${roomId}/recipients/${recipientId}/active-link`);
      await copyText(`${window.location.origin}/v/${res.token}`);
      flash("Link copied");
    } catch (e) {
      const msg = String((e as Error).message || e);
      if (msg.includes("link_token_unavailable") || msg.includes("no_active_link")) {
        flash("Resend the invite once to mint a copyable link.");
        return;
      }
      throw e;
    }
  };

  const resendInvite = async (recipientId: string) => {
    if (resendBusyRef.current) return;
    resendBusyRef.current = true;
    setResendBusyId(recipientId);
    try {
      await api(`/api/v1/rooms/${roomId}/recipients/${recipientId}/resend`, {
        method: "POST",
        body: JSON.stringify({ sendInvite: true }),
      });
      flash("Invite resent");
      await reload();
    } finally {
      resendBusyRef.current = false;
      setResendBusyId(null);
    }
  };

  const revoke = async (recipientId: string) => {
    if (revokeBusyRef.current) return;
    revokeBusyRef.current = true;
    setRevokeBusy(true);
    try {
      await api(`/api/v1/rooms/${roomId}/recipients/${recipientId}/revoke`, { method: "POST" });
      setRevokeId(null);
      await reload();
      flash("Access revoked");
    } finally {
      revokeBusyRef.current = false;
      setRevokeBusy(false);
    }
  };

  const loadLinkHistory = async (recipientId: string) => {
    const res = await api<{ links: LinkHistoryItem[] }>(`/api/v1/rooms/${roomId}/recipients/${recipientId}/links`);
    setLinkHistory((prev) => ({ ...prev, [recipientId]: res.links }));
  };

  const loadAnalytics = async () => {
    const forRoom = roomId;
    const gen = roomLoadGenRef.current;
    const res = await api<{ leaderboard: Recipient[]; documents: AnalyticsDoc[] }>(`/api/v1/analytics/rooms/${forRoom}`);
    if (gen !== roomLoadGenRef.current || roomIdRef.current !== forRoom) return;
    setLeaderboard(res.leaderboard);
    setAnalyticsDocs(res.documents ?? []);
  };

  const loadDocAnalytics = async (documentId: string) => {
    const forRoom = roomId;
    const req = ++docAnalyticsReqRef.current;
    setSelectedDocId(documentId);
    const res = await api<{ dropoff: Array<{ page: number; pct: number }> }>(
      `/api/v1/analytics/rooms/${forRoom}/documents/${documentId}`,
    );
    if (req !== docAnalyticsReqRef.current || roomIdRef.current !== forRoom) return;
    setDocDropoff(res.dropoff ?? []);
  };

  const viewAsVisitor = async (recipientId: string) => {
    try {
      const res = await api<{ token: string }>(`/api/v1/rooms/${roomId}/recipients/${recipientId}/active-link`);
      window.open(`${window.location.origin}/v/${res.token}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      const msg = String((e as Error).message || e);
      if (msg.includes("link_token_unavailable") || msg.includes("no_active_link")) {
        flash("Resend the invite once to mint a preview link.");
        return;
      }
      throw e;
    }
  };

  const saveBranding = async (patch: Record<string, unknown>, opts?: { quiet?: boolean }) => {
    await api(`/api/v1/rooms/${roomId}`, { method: "PATCH", body: JSON.stringify(patch) });
    // Quiet blur saves must not reload: reload resets brandingDraft and can wipe
    // an in-progress sibling field (Welcome → Banner/Logo).
    if (opts?.quiet) return;
    await reload();
    flash("Settings saved");
  };

  const patchBrandingDraft = (patch: Partial<typeof brandingDraft>) => {
    setBrandingDraft((prev) => ({ ...prev, ...patch }));
  };

  const toggleNdaRequired = async (enabled: boolean) => {
    if (!room) return;
    await api(`/api/v1/rooms/${roomId}`, { method: "PATCH", body: JSON.stringify({ ndaRequired: enabled }) });
    setRoom({ ...room, nda_required: enabled ? 1 : 0 });
    await loadNda();
    await reload();
    flash(enabled ? "NDA now required before viewing" : "NDA requirement removed");
  };

  const saveNda = async () => {
    const bodyHtml = ndaText.trim();
    if (!bodyHtml) {
      flash("NDA text cannot be empty");
      return;
    }
    setNdaSaving(true);
    try {
      await api(`/api/v1/rooms/${roomId}/nda`, { method: "PUT", body: JSON.stringify({ bodyHtml }) });
      if (room) setRoom({ ...room, nda_required: 1 });
      await loadNda();
      await reload();
      flash("NDA agreement saved");
    } finally {
      setNdaSaving(false);
    }
  };

  const filteredRecipients = useMemo(() => {
    const q = recipientQuery.trim().toLowerCase();
    let list = [...recipients];
    if (q) {
      list = list.filter(
        (r) => r.email.toLowerCase().includes(q) || (r.name?.toLowerCase().includes(q) ?? false),
      );
    }
    list.sort((a, b) => {
      if (recipientSort === "score") return Number(b.score || 0) - Number(a.score || 0);
      if (recipientSort === "email") return a.email.localeCompare(b.email);
      if (recipientSort === "status") return a.status.localeCompare(b.status);
      const at = new Date(a.last_active_at || 0).getTime();
      const bt = new Date(b.last_active_at || 0).getTime();
      return bt - at;
    });
    return list;
  }, [recipients, recipientQuery, recipientSort]);

  const saveLinkSettings = async (recipientId: string, patch: Record<string, unknown>) => {
    await api(`/api/v1/rooms/${roomId}/recipients/${recipientId}/link`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    await reload();
    flash("Link settings updated");
  };

  const loadAudit = async () => {
    const forRoom = roomId;
    const gen = roomLoadGenRef.current;
    const res = await api<{ events: Array<Record<string, unknown>> }>(`/api/v1/rooms/${forRoom}/audit`);
    if (gen !== roomLoadGenRef.current || roomIdRef.current !== forRoom) return;
    setAudit(res.events);
  };

  const cloneRoom = async () => {
    if (cloneBusyRef.current) return;
    cloneBusyRef.current = true;
    setCloneBusy(true);
    try {
      const res = await api<{ room: { id: string } }>(`/api/v1/rooms/${roomId}/clone`, {
        method: "POST",
        body: JSON.stringify({ name: `${room?.name} (copy)` }),
      });
      setCloneConfirm(false);
      window.location.href = `/rooms/${res.room.id}`;
    } finally {
      cloneBusyRef.current = false;
      setCloneBusy(false);
    }
  };

  const deleteRoom = async () => {
    if (!room || deleteName.trim() !== room.name) return;
    setDeleteBusy(true);
    try {
      await api(`/api/v1/rooms/${roomId}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmName: deleteName.trim() }),
      });
      setDeleteConfirm(false);
      navigate("/rooms", { replace: true });
    } finally {
      setDeleteBusy(false);
    }
  };

  const importCsv = async () => {
    if (importBusyRef.current) return;
    importBusyRef.current = true;
    setImportBusy(true);
    try {
      const lines = csvText.trim().split(/\n/).slice(1);
      const recipientsPayload = lines
        .map((line) => line.split(",").map((s) => s.trim().replace(/^"|"$/g, "")))
        .filter((cols) => cols[0])
        .map(([em, name, group, stage]) => ({ email: em, name, group, stage: stage || undefined }));
      await api(`/api/v1/rooms/${roomId}/recipients/import`, {
        method: "POST",
        body: JSON.stringify({ recipients: recipientsPayload }),
      });
      setCsvText("");
      setShowCsv(false);
      flash(`Imported ${recipientsPayload.length} recipient(s)`);
      await reload();
    } finally {
      importBusyRef.current = false;
      setImportBusy(false);
    }
  };

  if (loading && !room) return <SkeletonPage />;
  if (loadErr && !room) {
    return (
      <div className="panel stack">
        <h1 className="page-title">Space unavailable</h1>
        <p role="alert" className="muted">{loadErr}</p>
        <button type="button" className="secondary" onClick={() => reload().catch(console.error)}>
          Retry
        </button>
      </div>
    );
  }
  if (!room) return <SkeletonPage />;

  const readyCount = documents.filter((d) => d.status === "ready").length;
  const ndaPendingCount = recipients.filter(
    (r) => Boolean(r.require_nda ?? room.nda_required) && !r.nda_signed_at,
  ).length;
  const ndaSignedCount = ndaAcceptances.length;

  return (
    <div className="space-detail">
      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}

      <div className="row space-header" style={{ justifyContent: "space-between", gap: 12 }}>
        <div>
          <Link to="/rooms" className="muted room-crumb">
            ← Spaces
          </Link>
          <h1 className="page-title">{room.name}</h1>
          <p className="page-lead" style={{ marginBottom: 0 }}>
            {roomTypeLabel(room.room_type)} ·{" "}
            {readyCount === 0 && documents.length > 0
              ? `${documents.length} file${documents.length === 1 ? "" : "s"}, none ready yet`
              : `${readyCount} ready file${readyCount === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="row toolbar" style={{ gap: 8 }}>
          <button type="button" className="secondary" onClick={() => setCloneConfirm(true)}>
            Clone
          </button>
          <button
            type="button"
            className="ghost danger"
            onClick={() => {
              setDeleteName("");
              setDeleteConfirm(true);
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {setup && (
        <div className="panel setup-banner">
          <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
            <div>
              <strong>Get started</strong>
              <ol className="setup-steps">
                <li className={readyCount ? "done" : ""}>Upload documents</li>
                <li className={recipients.length ? "done" : ""}>Share a link</li>
                <li>Watch visits on Analytics</li>
              </ol>
            </div>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                searchParams.delete("setup");
                setSearchParams(searchParams, { replace: true });
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {readyCount > 0 && !recipients.length && tab === "content" && (
        <div className="panel ready-banner">
          <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
            <p style={{ margin: 0 }}>Content is ready. Share a link to start tracking views.</p>
            <button type="button" onClick={() => setTabAndUrl("recipients")}>
              Share
            </button>
          </div>
        </div>
      )}

      {room.nda_required && tab !== "access" ? (
        <div className="panel nda-signatures-bar">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <strong>NDA signatures</strong>
              {" "}
              <span className="muted" style={{ marginLeft: 6, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}>
                {ndaLoaded ? (
                  <>
                    {ndaSignedCount} signed{ndaPendingCount > 0 ? ` · ${ndaPendingCount} pending` : ""}
                  </>
                ) : (
                  <>
                    <BrandLoader size="sm" />
                    <span>Loading…</span>
                  </>
                )}
              </span>
            </div>
            <button type="button" className="secondary" onClick={() => setTabAndUrl("access")}>
              View signatures
            </button>
          </div>
        </div>
      ) : null}

      <div className="room-shell">
        <nav className="room-nav" aria-label="Space sections">
          {validTabs.map((t) => (
            <button
              key={t}
              type="button"
              aria-current={tab === t ? "page" : undefined}
              onClick={() => setTabAndUrl(t)}
            >
              {TAB_LABELS[t]}
              {t === "content" && readyCount > 0 ? <span className="room-nav__badge">{readyCount}</span> : null}
              {t === "recipients" && recipients.length > 0 ? (
                <span className="room-nav__badge">{recipients.length}</span>
              ) : null}
              {t === "access" && room.nda_required && ndaSignedCount > 0 ? (
                <span className="room-nav__badge">{ndaSignedCount}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="room-main" role="region" aria-label={TAB_LABELS[tab]}>
      {tab === "content" && (
        <FileBrowser
          roomId={roomId}
          folders={folders}
          documents={documents}
          folderId={folderId}
          onFolderChange={setFolderId}
          onReload={reload}
        />
      )}

      {tab === "recipients" && (
        <div className="stack">
          <section className="panel stack share-panel">
            <div className="section-head">
              <h3>Share access</h3>
            </div>
            <div className="mode-toggle" role="group" aria-label="Share mode">
              <button type="button" aria-pressed={shareMode === "link"} onClick={() => setShareMode("link")}>
                <span className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
                  <IconLink /> Copy and email
                </span>
              </button>
              <button type="button" aria-pressed={shareMode === "email"} onClick={() => setShareMode("email")}>
                Email only
              </button>
            </div>
            <form
              className="row form-row"
              onSubmit={(e) => {
                e.preventDefault();
                invite().catch((err) => flash(String((err as Error).message || err)));
              }}
            >
              <label className="grow stack" style={{ margin: 0 }}>
                Email
                <input className="input" type="email" placeholder="viewer@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label className="grow stack" style={{ margin: 0 }}>
                Name
                <input className="input" placeholder="Optional" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
              </label>
              <button type="submit" disabled={!email.trim() || inviteBusy}>
                {inviteBusy ? "Working…" : "Create invite"}
              </button>
            </form>
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              An invite email is always sent. Link settings: {room.nda_required ? "NDA required" : "No NDA"} · Downloads{" "}
              {room.download_policy === "allowed" ? "allowed" : room.download_policy === "exposé_pdf_only" ? "PDF only" : "off"} · Email verification on
            </p>
            {lastLink && (
              <div className="link-chip">
                <code>{lastLink}</code>
                <button type="button" className="ghost" onClick={() => copyText(lastLink)}>
                  Copy
                </button>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => window.open(lastLink, "_blank", "noopener,noreferrer")}
                >
                  View as visitor
                </button>
              </div>
            )}
            <button type="button" className="ghost" onClick={() => setShowCsv((v) => !v)}>
              {showCsv ? "Hide CSV import" : "Import CSV…"}
            </button>
            {showCsv && (
              <div className="stack">
                <label className="stack" style={{ margin: 0 }}>
                  CSV rows (email, name, group, stage)
                  <textarea className="input" rows={4} placeholder="email,name,group,stage" value={csvText} onChange={(e) => setCsvText(e.target.value)} />
                </label>
                <button
                  type="button"
                  className="secondary"
                  disabled={importBusy || !csvText.trim()}
                  onClick={() => importCsv().catch(console.error)}
                >
                  {importBusy ? "Importing…" : "Import"}
                </button>
              </div>
            )}
          </section>

          <section className="section" style={{ borderBottom: "none" }}>
            <div className="section-head">
              <h3>People with access</h3>
              {recipients.length > 0 ? (
                <div className="row toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
                  <label className="search-field" style={{ margin: 0 }}>
                    <IconSearch className="search-field__icon" />
                    <input
                      className="input"
                      placeholder="Search contacts"
                      value={recipientQuery}
                      onChange={(e) => setRecipientQuery(e.target.value)}
                      aria-label="Search contacts"
                    />
                  </label>
                  <select
                    className="input"
                    style={{ width: "auto", minWidth: 140 }}
                    value={recipientSort}
                    onChange={(e) => setRecipientSort(e.target.value as typeof recipientSort)}
                    aria-label="Sort contacts"
                  >
                    <option value="last_active">Last active</option>
                    <option value="score">Score</option>
                    <option value="email">Email</option>
                    <option value="status">Status</option>
                  </select>
                </div>
              ) : null}
            </div>
            {!recipients.length ? (
              <EmptyState
                headline="Invite your first viewer"
                body="Send an invite email. You can also copy the link after creating it."
                primaryLabel="Enter email"
                onPrimary={() => document.querySelector<HTMLInputElement>(".share-panel input")?.focus()}
                icon={<IconPerson />}
              />
            ) : filteredRecipients.length === 0 ? (
              <p className="muted">No contacts match &ldquo;{recipientQuery.trim()}&rdquo;</p>
            ) : (
              <div className="table-wrap">
                <table className="table table--responsive share-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Last active</th>
                      <th>Score</th>
                      <th>Docs</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipients.map((r) => {
                      const tone = recipientStatusTone(r.status);
                      const expanded = expandedRecipient === r.id;
                      return (
                        <Fragment key={r.id}>
                          <tr className={expanded ? "share-row--open" : undefined}>
                            <td data-label="Contact">
                              <button
                                type="button"
                                className="ghost share-row__toggle"
                                aria-expanded={expanded}
                                aria-controls={`link-settings-${r.id}`}
                                onClick={() => {
                                  const next = expanded ? null : r.id;
                                  setExpandedRecipient(next);
                                  if (next) loadLinkHistory(next).catch(console.error);
                                }}
                              >
                                {r.name || r.email}
                              </button>
                              {r.name ? <div className="muted" style={{ fontSize: 12 }}>{r.email}</div> : null}
                            </td>
                            <td data-label="Status">
                              <span className={`badge${tone ? ` ${tone}` : ""}`}>{recipientStatusLabel(r.status)}</span>
                              {linkExpiryBadge(r.link_expires_at)}
                              {Boolean(r.require_nda) &&
                                (r.nda_signed_at ? (
                                  <span
                                    className="badge ok"
                                    title={`Signed by ${r.nda_full_name ?? r.email} ${relativeTime(r.nda_signed_at)}`}
                                  >
                                    NDA signed
                                  </span>
                                ) : (
                                  <span className="badge warn">NDA pending</span>
                                ))}
                            </td>
                            <td className="muted" data-label="Last active">
                              {r.last_active_at ? relativeTime(r.last_active_at) : "n/a"}
                            </td>
                            <td data-label="Score">{r.score != null ? Math.round(Number(r.score)) : "n/a"}</td>
                            <td data-label="Docs">{r.docs_touched ?? 0}</td>
                            <td data-label="Actions">
                              <div className="row toolbar share-row__actions" style={{ flexWrap: "wrap", gap: 4 }}>
                                <button type="button" className="ghost" onClick={() => viewAsVisitor(r.id).catch((e) => flash(String(e.message || e)))}>
                                  Preview
                                </button>
                                <button type="button" className="ghost" onClick={() => copyRecipientLink(r.id).catch((e) => flash(String(e.message || e)))}>
                                  Copy
                                </button>
                                <button type="button" className="ghost" onClick={() => setRevokeId(r.id)}>
                                  Revoke
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expanded ? (
                            <tr className="share-settings-row">
                              <td colSpan={6}>
                                <div id={`link-settings-${r.id}`} className="link-settings panel stack">
                                  <strong style={{ fontSize: 13 }}>Link settings for {r.email}</strong>
                                  <div className="row" style={{ flexWrap: "wrap", gap: 16 }}>
                                    <label className="row" style={{ gap: 6 }}>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(r.require_nda ?? room.nda_required)}
                                        onChange={(e) =>
                                          saveLinkSettings(r.id, { requireNda: e.target.checked }).catch((err) =>
                                            flash(String(err.message || err)),
                                          )
                                        }
                                      />
                                      NDA required
                                    </label>
                                    <label className="row" style={{ gap: 6 }}>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(r.allow_download)}
                                        onChange={(e) =>
                                          saveLinkSettings(r.id, { allowDownload: e.target.checked }).catch((err) =>
                                            flash(String(err.message || err)),
                                          )
                                        }
                                      />
                                      Allow download
                                    </label>
                                    <label className="row" style={{ gap: 6 }}>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(r.watermark_enabled ?? 1)}
                                        onChange={(e) =>
                                          saveLinkSettings(r.id, { watermarkEnabled: e.target.checked }).catch((err) =>
                                            flash(String(err.message || err)),
                                          )
                                        }
                                      />
                                      Watermark
                                    </label>
                                  </div>
                                  <div className="grid-2" style={{ gap: 12 }}>
                                    <label className="stack" style={{ margin: 0 }}>
                                      Link expiry (optional)
                                      <input
                                        className="input"
                                        type="datetime-local"
                                        defaultValue={r.link_expires_at ? r.link_expires_at.slice(0, 16) : ""}
                                        onBlur={(e) => {
                                          const v = e.target.value;
                                          saveLinkSettings(r.id, { expiresAt: v ? new Date(v).toISOString() : null }).catch((err) =>
                                            flash(String(err.message || err)),
                                          );
                                        }}
                                      />
                                    </label>
                                    <label className="stack" style={{ margin: 0 }}>
                                      Link password (optional)
                                      <input
                                        className="input"
                                        type="password"
                                        placeholder={r.has_password ? "Set new password" : "No password"}
                                        onBlur={(e) => {
                                          const v = e.target.value.trim();
                                          if (!v) return;
                                          saveLinkSettings(r.id, { password: v }).catch((err) => flash(String(err.message || err)));
                                          e.target.value = "";
                                        }}
                                      />
                                    </label>
                                  </div>
                                  <div className="row toolbar" style={{ flexWrap: "wrap" }}>
                                    <button
                                      type="button"
                                      className="ghost"
                                      disabled={resendBusyId === r.id}
                                      onClick={() => resendInvite(r.id).catch(console.error)}
                                    >
                                      {resendBusyId === r.id ? "Sending…" : "Resend invite"}
                                    </button>
                                  </div>
                                  {(linkHistory[r.id] ?? []).length > 0 ? (
                                    <div className="link-history stack">
                                      <strong style={{ fontSize: 13 }}>Link history</strong>
                                      <div className="table-wrap">
                                        <table className="table table--compact">
                                          <thead>
                                            <tr>
                                              <th>Created</th>
                                              <th>Status</th>
                                              <th>Opens</th>
                                              <th>Last visit</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(linkHistory[r.id] ?? []).map((lnk) => (
                                              <tr key={lnk.id}>
                                                <td>{relativeTime(lnk.created_at)}</td>
                                                <td>
                                                  {lnk.revoked_at ? (
                                                    <span className="badge warn">Revoked</span>
                                                  ) : lnk.expires_at && new Date(lnk.expires_at).getTime() < Date.now() ? (
                                                    <span className="badge warn">Expired</span>
                                                  ) : (
                                                    <span className="badge ok">Active</span>
                                                  )}
                                                </td>
                                                <td>{lnk.session_count || lnk.use_count || 0}</td>
                                                <td className="muted">{lnk.last_session_at ? relativeTime(lnk.last_session_at) : "n/a"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "analytics" && (
        <section className="section" style={{ borderBottom: "none" }}>
          <div className="section-head">
            <h3>Engagement</h3>
            <a href={apiUrl(`/api/v1/analytics/rooms/${roomId}/export.csv`)}>Export CSV</a>
          </div>
          {!leaderboard.length && !analyticsDocs.length ? (
            <EmptyState
              headline="No visits yet"
              body="Share a recipient link to start tracking who is viewing your documents."
              primaryLabel="Share"
              onPrimary={() => setTabAndUrl("recipients")}
            />
          ) : (
            <div className="stack">
              {liveFeed.events.length === 0 ? (
                <section className="live-activity-panel live-activity-panel--empty panel">
                  <div className="live-activity-panel__head">
                    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                      <strong style={{ color: "var(--ds-text)", fontWeight: 500 }}>Live activity:</strong>{" "}
                      waiting for visitors. Opens and page views appear here in real time.
                    </p>
                    <span className={`live-activity-panel__status${liveFeed.connected ? " is-connected" : ""}`}>
                      {liveFeed.connected ? "Live" : liveFeed.everConnected ? "Reconnecting…" : "Connecting…"}
                    </span>
                  </div>
                </section>
              ) : (
              <section className="live-activity-panel panel">
                <div className="live-activity-panel__head">
                  <h3 style={{ margin: 0 }}>Live activity</h3>
                  <span className={`live-activity-panel__status${liveFeed.connected ? " is-connected" : ""}`}>
                    {liveFeed.connected ? "Live" : liveFeed.everConnected ? "Reconnecting…" : "Connecting…"}
                  </span>
                </div>
                  <ul className="live-activity-list">
                    {liveFeed.events.slice(0, 12).map((ev, i) => {
                      const who = ev.visitorName || ev.visitorEmail || "Someone";
                      let detail = `${who} opened this Space`;
                      if (ev.type === "page_view") {
                        const doc = ev.documentName || "a document";
                        detail = ev.pageNumber
                          ? `${who} on page ${ev.pageNumber} of ${doc}`
                          : `${who} viewing ${doc}`;
                      }
                      return (
                        <li key={`${ev.viewId}-${ev.type}-${ev.pageNumber}-${ev.at}-${i}`}>
                          <span>{detail}</span>
                          <time className="muted">{liveFeed.formatTime(ev.at)}</time>
                        </li>
                      );
                    })}
                  </ul>
              </section>
              )}
              {leaderboard.length > 0 ? (
                <div className="table-wrap">
                  <table className="table table--responsive">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Score</th>
                        <th>Label</th>
                        <th>Last active</th>
                        <th>7d</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((r) => (
                        <tr key={r.id}>
                          <td data-label="Recipient">{r.name || r.email}</td>
                          <td data-label="Score">{Math.round(Number(r.score || 0))}</td>
                          <td data-label="Label">
                            <span className="badge">{r.label}</span>
                          </td>
                          <td className="muted" data-label="Last active">{r.last_active_at ? relativeTime(r.last_active_at) : "n/a"}</td>
                          <td data-label="7d">{Number(r.momentum_7d || 0) > 0 ? "↑" : Number(r.momentum_7d || 0) < 0 ? "↓" : "→"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {analyticsDocs.length > 0 ? (
                <section className="panel stack">
                  <h3 style={{ margin: 0 }}>Document engagement</h3>
                  <div className="table-wrap">
                    <table className="table table--responsive">
                      <thead>
                        <tr>
                          <th>Document</th>
                          <th>Viewers</th>
                          <th>Dwell</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsDocs.map((d) => (
                          <tr
                            key={d.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => loadDocAnalytics(d.id).catch(console.error)}
                          >
                            <td data-label="Document">{d.filename}</td>
                            <td data-label="Viewers">{d.unique_viewers}</td>
                            <td data-label="Dwell">{Math.round(Number(d.total_dwell || 0) / 1000)}s</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {selectedDocId && docDropoff.length > 0 ? (
                    <div className="stack">
                      <h4 style={{ margin: "8px 0 0" }}>Page reach</h4>
                      {docDropoff.map((p) => (
                        <div key={p.page} className="dropoff-bar">
                          <span style={{ width: 48 }}>p.{p.page}</span>
                          <div className="dropoff-bar__track">
                            <div className="dropoff-bar__fill" style={{ width: `${p.pct}%` }} />
                          </div>
                          <span>{p.pct}%</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>
          )}
        </section>
      )}

      {tab === "access" && (
        <div className="stack access-settings">
          <section className="panel stack access-settings__policy">
            <h3 style={{ margin: 0 }}>Download policy</h3>
            <p className="muted" style={{ margin: 0 }}>
              Controls whether visitors can download files after they pass the access gates.
            </p>
            <select
              className="input"
              style={{ maxWidth: 320 }}
              aria-label="Download policy"
              value={room.download_policy}
              onChange={async (e) => {
                const downloadPolicy = e.target.value;
                await api(`/api/v1/rooms/${roomId}`, {
                  method: "PATCH",
                  body: JSON.stringify({ downloadPolicy }),
                });
                setRoom({ ...room, download_policy: downloadPolicy });
                flash("Download policy updated");
              }}
            >
              <option value="view_only">View only</option>
              <option value="allowed">Allow download</option>
              <option value="exposé_pdf_only">Exposé PDF only</option>
            </select>
          </section>

          <section className="panel stack">
            <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ margin: 0 }}>Non-disclosure agreement</h3>
                <p className="muted" style={{ margin: "4px 0 0" }}>
                  When enabled, every invited visitor must sign the NDA after verifying their email, before any
                  document opens.
                </p>
              </div>
              <label className="row" style={{ gap: 8, whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={Boolean(room.nda_required)}
                  onChange={(e) => toggleNdaRequired(e.target.checked).catch(console.error)}
                />
                Require NDA
              </label>
            </div>
            {room.nda_required ? (
              <>
                <label className="stack">
                  Agreement text (HTML allowed)
                  <textarea
                    className="input"
                    rows={6}
                    value={ndaText}
                    placeholder={ndaLoaded ? "Enter the confidentiality terms visitors must accept." : "Loading…"}
                    onChange={(e) => setNdaText(e.target.value)}
                  />
                </label>
                <div className="row" style={{ gap: 8 }}>
                  <button type="button" className="primary" disabled={ndaSaving} onClick={() => saveNda().catch(console.error)}>
                    {ndaSaving ? "Saving…" : "Save agreement"}
                  </button>
                  <span className="muted" style={{ fontSize: 12 }}>
                    Editing publishes a new version. Prior signatures stay on record.
                  </span>
                </div>
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                No agreement required. Invited visitors get direct access after email verification.
              </p>
            )}
          </section>

          <div className="access-branding-grid">
            <section className="panel stack">
              <h3 style={{ margin: 0 }}>Visitor experience</h3>
              <label className="stack">
                Welcome message
                <textarea
                  className="input"
                  rows={3}
                  value={brandingDraft.welcome_message}
                  onChange={(e) => patchBrandingDraft({ welcome_message: e.target.value })}
                  onBlur={() => saveBranding({ welcomeMessage: brandingDraft.welcome_message }, { quiet: true }).catch(console.error)}
                />
              </label>
              <label className="stack">
                Banner image URL
                <input
                  className="input"
                  value={brandingDraft.banner_url}
                  placeholder="https://…"
                  onChange={(e) => patchBrandingDraft({ banner_url: e.target.value })}
                  onBlur={() => saveBranding({ bannerUrl: brandingDraft.banner_url || null }, { quiet: true }).catch(console.error)}
                />
              </label>
              <label className="stack">
                Logo URL
                <input
                  className="input"
                  value={brandingDraft.logo_url}
                  placeholder="https://…"
                  onChange={(e) => patchBrandingDraft({ logo_url: e.target.value })}
                  onBlur={() => saveBranding({ logoUrl: brandingDraft.logo_url || null }, { quiet: true }).catch(console.error)}
                />
              </label>
              <label className="row" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  checked={brandingDraft.watermark_enabled}
                  onChange={(e) => {
                    patchBrandingDraft({ watermark_enabled: e.target.checked });
                    saveBranding({ watermarkEnabled: e.target.checked }, { quiet: true }).catch(console.error);
                  }}
                />
                Enable forensic watermarks for visitors
              </label>
            </section>
            <VisitorGatePreview
              showLabel
              roomName={room.name}
              welcomeMessage={brandingDraft.welcome_message}
              bannerUrl={brandingDraft.banner_url}
              logoUrl={brandingDraft.logo_url}
              ndaRequired={Boolean(room.nda_required)}
              ndaTitle={ndaTitle}
              ndaBodyHtml={ndaText}
            />
          </div>

          {room.nda_required ? (
            <section className="panel stack nda-signatures-panel">
              <div className="section-head">
                <div>
                  <h3 style={{ margin: 0 }}>NDA signatures</h3>
                  <p className="muted" style={{ margin: "4px 0 0" }}>
                    Legal names and drawn signatures captured when visitors accept the NDA.
                    A PDF receipt is emailed to admins on each signature and can be downloaded below.
                  </p>
                </div>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  {ndaSignedCount > 0 ? (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        api<{ built: number; attempted: number; failed: string[] }>(
                          `/api/v1/rooms/${roomId}/nda/receipts/backfill`,
                          { method: "POST", body: JSON.stringify({}) },
                        )
                          .then((r) => {
                            flash(`PDF receipts ready: ${r.built} built (${r.attempted} signatures)`);
                            return loadNda();
                          })
                          .catch((e) => flash(String((e as Error).message || e)))
                      }
                    >
                      Rebuild missing PDFs
                    </button>
                  ) : null}
                  <span className="badge ok">{ndaSignedCount} signed</span>
                </div>
              </div>
              {!ndaLoaded ? (
                <div className="brand-loader-inline">
                  <BrandLoader label="Loading signatures…" size="md" />
                </div>
              ) : ndaAcceptances.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>No one has signed yet.</p>
              ) : (
                <div className="nda-signatures-grid">
                  {ndaAcceptances.map((a) => (
                    <article key={a.id} className="nda-signature-card">
                      <div className="nda-signature-card__meta">
                        <strong>{a.full_name}</strong>
                        <span className="muted">{a.recipient_email}</span>
                        <span className="muted">
                          v{a.version} · {relativeTime(a.accepted_at)}
                        </span>
                        <button
                          type="button"
                          className="section-head__action"
                          style={{ alignSelf: "flex-start", marginTop: 4 }}
                          onClick={() =>
                            downloadDocument(
                              `/api/v1/rooms/${roomId}/nda/acceptances/${a.id}/receipt`,
                              `NDA-receipt-${a.full_name.replace(/[^\w.-]+/g, "_").slice(0, 48)}.pdf`,
                              { admin: true },
                            ).catch((e) => flash(String((e as Error).message || e)))
                          }
                        >
                          Download PDF receipt
                        </button>
                      </div>
                      {a.signature_data_url ? (
                        <img
                          src={a.signature_data_url}
                          alt={`Signature of ${a.full_name}`}
                          className="nda-signature-card__img"
                        />
                      ) : (
                        <p className="muted nda-signature-card__empty">Name accepted, no drawn signature</p>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          ) : null}
        </div>
      )}

      {tab === "audit" && (
        <section className="section" style={{ borderBottom: "none" }}>
          <div className="section-head">
            <h3>Audit</h3>
            {audit.length ? (
              <span className="muted" style={{ fontSize: 13 }}>
                {audit.length} event{audit.length === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          {!audit.length ? (
            <EmptyState headline="Activity will show up here" body="Uploads, invites, views, and access changes are logged automatically." />
          ) : (
            <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
              <div className="table-wrap" style={{ border: "none", borderRadius: 0 }}>
                <table className="table table--responsive">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>Actor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((e, i) => (
                      <tr key={i}>
                        <td className="muted" data-label="When" title={String(e.created_at)}>
                          {relativeTime(String(e.created_at))}
                        </td>
                        <td data-label="Action">{auditActionLabel(e.action)}</td>
                        <td data-label="Actor">{auditActorLabel(e)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}
        </div>
      </div>

      {cloneConfirm && (
        <Modal
          title="Clone space"
          onClose={() => setCloneConfirm(false)}
          primaryLabel={cloneBusy ? "Cloning…" : "Clone"}
          primaryDisabled={cloneBusy}
          onPrimary={() => cloneRoom().catch((e) => flash(String(e.message || e)))}
        >
          <p className="muted" style={{ margin: 0 }}>
            Create a copy of folder structure from “{room.name}”? Documents are not copied.
          </p>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal
          title="Delete space permanently"
          onClose={() => setDeleteConfirm(false)}
          primaryLabel={deleteBusy ? "Deleting…" : "Delete permanently"}
          primaryDisabled={deleteBusy || deleteName.trim() !== room.name}
          danger
          onPrimary={() => deleteRoom().catch((e) => flash(String(e.message || e)))}
        >
          <div className="stack">
            <p className="muted" style={{ margin: 0 }}>
              This permanently deletes the Space, all files, visitor data, NDA signatures, analytics, and
              access links. This cannot be undone (GDPR/FADP erasure).
            </p>
            <label className="stack">
              Type <strong>{room.name}</strong> to confirm
              <input
                className="input"
                value={deleteName}
                autoFocus
                placeholder={room.name}
                onChange={(e) => setDeleteName(e.target.value)}
              />
            </label>
          </div>
        </Modal>
      )}

      {shareEmptyConfirm && (
        <Modal
          title="No ready documents"
          onClose={() => setShareEmptyConfirm(false)}
          primaryLabel="Share anyway"
          onPrimary={() => {
            setShareEmptyConfirm(false);
            doInvite().catch((e) => flash(String(e.message || e)));
          }}
        >
          <p className="muted" style={{ margin: 0 }}>
            This space has no ready documents yet. Share anyway?
          </p>
        </Modal>
      )}

      {revokeId && (
        <Modal
          title="Revoke access"
          onClose={() => setRevokeId(null)}
          primaryLabel={revokeBusy ? "Revoking…" : "Revoke"}
          primaryDisabled={revokeBusy}
          danger
          onPrimary={() => revoke(revokeId).catch((e) => flash(String(e.message || e)))}
        >
          <p className="muted" style={{ margin: 0 }}>
            Their links will stop working immediately. You can invite them again later.
          </p>
        </Modal>
      )}
    </div>
  );
}
